// lint rule not applicable to V2 components
/* eslint-disable react/forbid-elements */
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import Link from 'next/link';

import Icon, { IconName } from '@/components/Icon/Icon';
import { Footer, SocialMediaLink } from '@/contentful';
import { openLinkCurrentTab } from '@/contentful/utils';
import useDeviceWidth from '@/hooks/deviceWidth';
import TextLogo from '@/public/cmsAssets/assets/img/logo/TextLogo';
import { brandColors } from '@/styles/sunrunTheme.styles';
import { trackEvents } from '@/utils/analytics';

import useStyles from './Footer.styles';

interface SocialIconsProps {
  socialLinks: SocialMediaLink[];
}

/**
 * @deprecated V2 component
 */
function SocialIcons({ socialLinks }: SocialIconsProps) {
  const { classes } = useStyles();
  {
    /* These could be handled more elegantly as they currently rely on the name being correct */
  }
  return (
    <div className={classes.socialIcons}>
      <a
        href={
          socialLinks?.find(
            (socialLink) => socialLink.fields.title === 'Facebook'
          )?.fields.url
        }
        target='_blank'
        rel='noopener noreferrer'
        className={classes.socialIcon}
      >
        <Icon name={IconName.FACEBOOK} width={20} />
      </a>
      <a
        href={
          socialLinks?.find(
            (socialLink) => socialLink.fields.title === 'Twitter'
          )?.fields.url
        }
        target='_blank'
        rel='noopener noreferrer'
        className={classes.socialIcon}
      >
        <Icon name={IconName.TWITTER} width={20} />
      </a>
      <a
        href={
          socialLinks?.find(
            (socialLink) => socialLink.fields.title === 'Instagram'
          )?.fields.url
        }
        target='_blank'
        rel='noopener noreferrer'
        className={classes.socialIcon}
      >
        <Icon name={IconName.INSTAGRAM} width={20} />
      </a>
      <a
        href={
          socialLinks?.find(
            (socialLink) => socialLink.fields.title === 'LinkedIn'
          )?.fields.url
        }
        target='_blank'
        rel='noopener noreferrer'
        className={classes.socialIcon}
      >
        <Icon name={IconName.LINKEDIN} width={20} />
      </a>
    </div>
  );
}

export interface Props {
  content: Footer;
}
/**
 * @deprecated use Global Footer component
 */
const LegacyFooter = ({ content }: Props) => {
  const { isMobile } = useDeviceWidth();
  const { classes } = useStyles();
  const { socialLinks, categories, legalLinks, copyrightText } =
    content?.fields || {};

  return (
    <footer className={classes.footer}>
      <div className={classes.footerContent}>
        <div className={classes.linkSection}>
          {categories?.map((category) => {
            return isMobile ? (
              <Accordion key={category.sys.id} className={classes.accordion}>
                <AccordionSummary style={{}}>
                  <h3 className={classes.linkCategory}>
                    {category.fields.name}
                  </h3>
                </AccordionSummary>
                <AccordionDetails
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {category?.fields?.links?.map((link) => {
                    const linkTarget = openLinkCurrentTab(link.fields.url)
                      ? ''
                      : '_blank';
                    const linkRef = openLinkCurrentTab(link.fields.url)
                      ? ''
                      : 'noopener noreferrer';
                    return (
                      <div className={classes.linkText} key={link.sys.id}>
                        <a
                          href={link.fields.url}
                          target={linkTarget}
                          rel={linkRef}
                          onClick={() => {
                            trackEvents({
                              event: 'Footer click',
                              click_text_category: category.fields.name,
                              click_text: link.fields.text,
                            });
                          }}
                        >
                          {link.fields.text}
                        </a>
                      </div>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ) : (
              <div className={classes.linkColumn} key={category.sys.id}>
                <h3 className={classes.linkCategory}>{category.fields.name}</h3>
                {category?.fields?.links?.map((link) => {
                  const isCareer = link.fields.url.startsWith(
                    'https://careers.sunrun.com/us/en'
                  );
                  const linkTarget = openLinkCurrentTab(link.fields.url)
                    ? ''
                    : '_blank';
                  const linkRef = openLinkCurrentTab(link.fields.url)
                    ? ''
                    : 'noopener noreferrer';
                  return (
                    <div className={classes.linkText} key={link.sys.id}>
                      <a
                        href={link.fields.url}
                        target={isCareer ? '_blank' : linkTarget}
                        rel={isCareer ? 'noopener noreferrer' : linkRef}
                      >
                        {link.fields.text}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {isMobile && <SocialIcons socialLinks={socialLinks} />}
        <div className={classes.legal}>
          {legalLinks?.fields?.links?.map((legalLink, index) => {
            const linkTarget = openLinkCurrentTab(legalLink.fields.url)
              ? ''
              : '_blank';
            const linkRef = openLinkCurrentTab(legalLink.fields.url)
              ? ''
              : 'noopener noreferrer';
            return (
              <a
                href={legalLink.fields.url}
                key={legalLink.sys.id}
                target={linkTarget}
                rel={linkRef}
                onClick={() => {
                  trackEvents({
                    event: 'Legal link click',
                    click_text: legalLink.fields.text,
                  });
                }}
              >
                {legalLink.fields.text}
                {index !== legalLinks.fields.links.length - 1 && ' / '}
              </a>
            );
          })}
        </div>
        <p className={classes.copyright}>{copyrightText}</p>
        <div className={classes.bottomSection}>
          <Link href='/'>
            <TextLogo
              color={brandColors.offWhite}
              width='110'
              classname={classes.logo}
            />
          </Link>
          {!isMobile && <SocialIcons socialLinks={socialLinks} />}
        </div>
      </div>
    </footer>
  );
};

export default LegacyFooter;
